// =============================================================================
// assetExcel.js
// Excel template export / import for the asset register (bud_assets).
//
// Flat layout — one row per asset, components included as ordinary rows that
// name their parent in the "Part Of" column. Site, Area, Class, Cost Centre,
// Work Centre, Criticality and Active all carry in-cell dropdowns backed by a
// hidden "Lists" sheet, the same way the customer and volume templates work.
//
// Two things are specific to this register:
//
//   * PARENTS MUST COME FIRST. The importer sorts them so a machine and its
//     component can arrive in the same file in any order, but the exported file
//     is already in parent-then-component order so a round trip stays readable.
//   * AREA IS IGNORED ON A COMPONENT. The database copies a component's area
//     down from its parent, so the template greys the value out rather than
//     pretending it can be set.
//
// Import returns { rows, errors }; `rows` is empty when there are any errors so
// the caller can refuse a partial import.
// =============================================================================
import ExcelJS from 'exceljs';

const DELIM = ' — ';       // separates "CODE — Description" in dropdown values
const NONE = '(None)';     // explicit "not set" choice
const BLANK_ROWS = 200;    // empty rows (with dropdowns) appended after the data
const ACTIVE_OPTS = ['Active', 'Inactive'];
const CRIT_OPTS = ['A — Production stops', 'B — Degraded output', 'C — Nuisance only', NONE];

// "FORKLIFT — Forklifts & lifting" -> "FORKLIFT"; plain "FORKLIFT" -> "FORKLIFT".
const codeOf = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  const i = s.indexOf(DELIM);
  return (i >= 0 ? s.slice(0, i) : s).trim();
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ExcelJS cell.value may be a string, number, or rich-text/formula object.
const cellText = (cell) => {
  const v = cell?.value;
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') return String(v.text ?? v.result ?? '');
  return String(v);
};

const boolToLabel = (v) => (v === false ? 'Inactive' : 'Active');
const labelToBool = (raw) => String(raw).trim().toLowerCase() !== 'inactive';

// Excel column letter for a 0-based index (A..Z, AA..).
const colLetter = (idx) => {
  let n = idx + 1;
  let s = '';
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
};

// `list` names the hidden-sheet column a dropdown reads from.
const COLUMNS = [
  { key: 'asset_code',        header: 'Asset Code',        width: 18, required: true },
  { key: 'description',       header: 'Description',       width: 34, required: true },
  { key: 'site_code',         header: 'Site',              width: 26, list: 'sites', required: true },
  { key: 'area_code',         header: 'Area',              width: 26, list: 'areas' },
  { key: 'parent_asset_code', header: 'Part Of',           width: 18 },
  { key: 'asset_class_code',  header: 'Class',             width: 30, list: 'classes' },
  { key: 'cost_center_code',  header: 'Cost Centre',       width: 28, list: 'costCenters' },
  { key: 'work_center_code',  header: 'Work Centre',       width: 26, list: 'workCenters' },
  { key: 'criticality',       header: 'Criticality',       width: 22, crit: true },
  { key: 'meter_uom_own',     header: 'Meter (override)',  width: 16 },
  { key: 'replacement_value', header: 'Replacement Value', width: 18, numeric: true },
  { key: 'commissioned_date', header: 'Commissioned',      width: 14, date: true },
  { key: 'manufacturer',      header: 'Manufacturer',      width: 20 },
  { key: 'model',             header: 'Model',             width: 18 },
  { key: 'serial_number',     header: 'Serial No',         width: 18 },
  { key: 'is_active',         header: 'Active',            width: 10, active: true },
  { key: 'notes',             header: 'Notes',             width: 40 },
];

const SHEET = 'Assets';

// Hidden-sheet columns, in order. Each is [key, valuesFn].
const LIST_SPECS = [
  ['sites', (o) => (o.sites || []).map((s) => `${s.code}${DELIM}${s.description ?? ''}`)],
  ['areas', (o) => (o.areas || []).map((a) => `${a.code}${DELIM}${a.description ?? ''}`)],
  ['classes', (o) => (o.classes || []).map((c) => `${c.code}${DELIM}${c.description ?? ''}`)],
  ['costCenters', (o) => (o.costCenters || []).map((c) => `${c.code}${DELIM}${c.description ?? ''}`)],
  ['workCenters', (o) => (o.workCenters || []).map((c) => `${c.code}${DELIM}${c.description ?? ''}`)],
];

const labelMap = (opts = []) =>
  Object.fromEntries(opts.map((o) => [o.code, `${o.code}${DELIM}${o.description ?? ''}`]));

// ---- Export -----------------------------------------------------------------
// `assets` are records straight from the register load; the remaining lists
// populate the dropdowns. `versionLabel` only decorates the filename.
export async function exportAssetTemplate({
  assets = [], sites = [], areas = [], classes = [], costCenters = [], workCenters = [],
  versionLabel = '', blankRows = BLANK_ROWS,
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet(SHEET, { views: [{ state: 'frozen', ySplit: 1, xSplit: 1 }] });
  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';

  // Build the hidden lists, remembering each one's column and length so the
  // data-validation formulae can point at the exact range.
  const opts = { sites, areas, classes, costCenters, workCenters };
  const listRange = {};
  LIST_SPECS.forEach(([key, valuesFn], i) => {
    const values = [NONE, ...valuesFn(opts)];
    const letter = colLetter(i);
    lists.getCell(`${letter}1`).value = key;
    values.forEach((v, r) => { lists.getCell(`${letter}${r + 2}`).value = v; });
    listRange[key] = `=Lists!$${letter}$2:$${letter}$${values.length + 1}`;
  });
  const critLetter = colLetter(LIST_SPECS.length);
  lists.getCell(`${critLetter}1`).value = 'criticality';
  CRIT_OPTS.forEach((v, r) => { lists.getCell(`${critLetter}${r + 2}`).value = v; });
  const critRange = `=Lists!$${critLetter}$2:$${critLetter}$${CRIT_OPTS.length + 1}`;

  const activeLetter = colLetter(LIST_SPECS.length + 1);
  lists.getCell(`${activeLetter}1`).value = 'active';
  ACTIVE_OPTS.forEach((v, r) => { lists.getCell(`${activeLetter}${r + 2}`).value = v; });
  const activeRange = `=Lists!$${activeLetter}$2:$${activeLetter}$${ACTIVE_OPTS.length + 1}`;

  ws.columns = COLUMNS.map((c) => ({ header: c.header, key: c.key, width: c.width }));

  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
  });

  const maps = {
    sites: labelMap(sites), areas: labelMap(areas), classes: labelMap(classes),
    costCenters: labelMap(costCenters), workCenters: labelMap(workCenters),
  };

  // Parents before their own components, so the file reads top-down.
  const ordered = [...assets].sort((a, b) => {
    const ka = a.parent_asset_code || a.asset_code;
    const kb = b.parent_asset_code || b.asset_code;
    if (ka !== kb) return ka.localeCompare(kb);
    return (a.parent_asset_code ? 1 : 0) - (b.parent_asset_code ? 1 : 0);
  });

  ordered.forEach((r, i) => {
    const row = ws.getRow(i + 2);
    const isComponent = !!r.parent_asset_code;
    COLUMNS.forEach((col, ci) => {
      const cell = row.getCell(ci + 1);
      const v = r[col.key];
      if (col.active) cell.value = boolToLabel(v);
      else if (col.crit) cell.value = v ? (CRIT_OPTS.find((o) => codeOf(o) === v) || v) : NONE;
      else if (col.list) cell.value = v ? (maps[col.list][v] || v) : NONE;
      else if (col.numeric) cell.value = v === null || v === undefined || v === '' ? null : Number(v);
      else cell.value = v ?? '';
    });
    if (isComponent) {
      // Area is inherited, not settable — grey it so nobody edits it in vain.
      row.getCell(COLUMNS.findIndex((c) => c.key === 'area_code') + 1).font = { color: { argb: 'FF9CA3AF' }, italic: true };
      row.getCell(1).font = { color: { argb: 'FF475569' } };
    }
  });

  // Dropdowns across the data rows plus the blank rows underneath.
  const lastRow = ordered.length + blankRows + 1;
  COLUMNS.forEach((col, ci) => {
    let formula = null;
    if (col.list) formula = listRange[col.list];
    else if (col.crit) formula = critRange;
    else if (col.active) formula = activeRange;
    if (!formula) return;
    const letter = colLetter(ci);
    for (let r = 2; r <= lastRow; r++) {
      ws.getCell(`${letter}${r}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [formula],
        showErrorMessage: true, errorStyle: 'warning',
        error: `Pick a ${col.header} from the list.`, errorTitle: 'Pick from the list',
      };
    }
  });

  ws.autoFilter = { from: 'A1', to: `${colLetter(COLUMNS.length - 1)}1` };

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const suffix = versionLabel ? `_${String(versionLabel).replace(/[^\w-]+/g, '')}` : '';
  triggerDownload(blob, `Asset_Register${suffix}.xlsx`);
}

// ---- Import -----------------------------------------------------------------
// Returns { rows, errors }. Each row matches the /api/assets upsert payload.
// Dimension values are validated against the lists so a typo fails here with a
// row number rather than as a foreign-key error from Postgres.
export async function parseAssetTemplate(file, {
  sites = [], areas = [], classes = [], costCenters = [], workCenters = [],
} = {}) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet(SHEET) || wb.worksheets[0];
  if (!ws) return { rows: [], errors: ['Could not find a worksheet in the file.'] };

  const sets = {
    sites: new Set(sites.map((s) => s.code)),
    areas: new Set(areas.map((a) => a.code)),
    classes: new Set(classes.map((c) => c.code)),
    costCenters: new Set(costCenters.map((c) => c.code)),
    workCenters: new Set(workCenters.map((c) => c.code)),
  };
  const listLabel = {
    sites: 'Site', areas: 'Area', classes: 'Class',
    costCenters: 'Cost Centre', workCenters: 'Work Centre',
  };

  const out = [];
  const errors = [];
  const seen = new Set();

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    const raw = COLUMNS.map((_, ci) => cellText(row.getCell(ci + 1)));
    if (!raw.some((s) => String(s).trim())) return;          // blank row
    // A row carrying only "(None)" dropdown defaults is blank in every way that
    // matters — the template ships 200 of them.
    if (!String(raw[0]).trim() && !String(raw[1]).trim()) return;

    const rowErrs = [];
    const rec = {};

    COLUMNS.forEach((col, ci) => {
      const text = String(raw[ci]).trim();

      if (col.active) { rec[col.key] = labelToBool(text || 'Active'); return; }

      if (col.crit) {
        const c = text && text !== NONE ? codeOf(text).toUpperCase() : null;
        if (c && !['A', 'B', 'C'].includes(c)) rowErrs.push(`invalid Criticality "${text}"`);
        rec[col.key] = c;
        return;
      }

      if (col.list) {
        if (!text || text === NONE) {
          if (col.required) rowErrs.push(`missing ${col.header}`);
          rec[col.key] = null;
          return;
        }
        const code = codeOf(text);
        if (!sets[col.list].has(code)) rowErrs.push(`unknown ${listLabel[col.list]} "${code}"`);
        rec[col.key] = code;
        return;
      }

      if (col.numeric) {
        if (!text) { rec[col.key] = null; return; }
        const n = Number(String(text).replace(/[\s,]/g, ''));
        if (Number.isNaN(n) || n < 0) rowErrs.push(`invalid ${col.header} "${text}"`);
        rec[col.key] = Number.isNaN(n) ? null : n;
        return;
      }

      if (col.date) {
        if (!text) { rec[col.key] = null; return; }
        const m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) rowErrs.push(`invalid ${col.header} "${text}" — use YYYY-MM-DD`);
        rec[col.key] = m ? m[0] : null;
        return;
      }

      if (!text && col.required) rowErrs.push(`missing ${col.header}`);
      rec[col.key] = text || null;
    });

    if (rec.asset_code) {
      rec.asset_code = rec.asset_code.toUpperCase();
      if (seen.has(rec.asset_code)) rowErrs.push(`duplicate Asset Code "${rec.asset_code}" in file`);
      else seen.add(rec.asset_code);
    }
    if (rec.parent_asset_code) rec.parent_asset_code = rec.parent_asset_code.toUpperCase();
    if (rec.parent_asset_code && rec.parent_asset_code === rec.asset_code) {
      rowErrs.push('an asset cannot be its own parent');
    }

    if (rowErrs.length) { errors.push(`Row ${rowNumber}: ${rowErrs.join(', ')}`); return; }
    out.push(rec);
  });

  if (errors.length) return { rows: [], errors };

  // Parents first, so a machine and its component can be imported together. A
  // parent that is in neither the file nor the register is left to the server's
  // foreign key — validating it here would wrongly reject files that add a
  // component to a machine already in the register.
  out.sort((a, b) => (a.parent_asset_code ? 1 : 0) - (b.parent_asset_code ? 1 : 0));
  return { rows: out, errors: [] };
}

export default { exportAssetTemplate, parseAssetTemplate };
